// Замер расхождения часов клиента и сервера.
//
// Моноблок на кухне никто не сверяет с эталоном, а на нём считается срок годности:
// без замера расхождение обнаружится испорченным продуктом или выброшенным годным.
// Меряется на КАЖДОМ синке и уезжает в `PrintJob.clock_offset`, записи сверх порога
// помечаются в журнале.

import { onServerTime } from '@/shared/api';

import { offlineDb } from './db';

/** Порог, за которым запись помечается как сделанная на сбитых часах. Секунды. */
export const CLOCK_DRIFT_THRESHOLD_SECONDS = 300;

/**
 * Расхождение в секундах: положительное — часы клиента спешат.
 *
 * Замер грубый и таким и должен быть: сетевая задержка входит в него целиком,
 * а точность в пределах секунд для порога в пять минут несущественна. Тонкий
 * протокол вроде NTP здесь был бы машинерией ради машинерии.
 */
export function measureDrift(clientNow: Date, serverNow: Date): number {
  return Math.round((clientNow.getTime() - serverNow.getTime()) / 1000);
}

export function isDrifted(offsetSeconds: number): boolean {
  return Math.abs(offsetSeconds) > CLOCK_DRIFT_THRESHOLD_SECONDS;
}

/** Запомнить последний замер в кэше сессии. */
export async function rememberDrift(offsetSeconds: number, at: Date = new Date()): Promise<void> {
  const current = await offlineDb.session.get('current');
  if (!current) {
    // Профиля ещё нет — мерить дрейф не для кого. Раньше здесь создавалась запись
    // с одними часами и пустой подпиской, и `evaluateLicense` читал её как «лицензия
    // неизвестна»: измерение часов молча превращалось в отказ печати.
    return;
  }

  await offlineDb.session.update('current', {
    clockOffset: offsetSeconds,
    lastSyncAt: at.toISOString(),
  });
}

export async function currentDrift(): Promise<number> {
  return (await offlineDb.session.get('current'))?.clockOffset ?? 0;
}

/**
 * Начать мерить дрейф на каждом ответе сервера.
 *
 * Вызывается из bootstrap ОДИН раз. До этого `rememberDrift` не вызывался из приложения
 * ВОВСЕ — только из тестов, — и «дрейф меряется на каждом синке» было заявлением,
 * а не поведением. Вместе с ним не заполнялось `lastSyncAt`, а на нём стоит весь
 * grace-период: `evaluateLicense` читал пустоту и отвечал «ни разу не синкались».
 *
 * То есть неподключённый замер часов молча блокировал бы печать, если бы саму проверку
 * лицензии кто-нибудь подключил. Две мёртвые связи держали друг друга.
 */
export function startClockSync(): () => void {
  return onServerTime((serverNow) => {
    void rememberDrift(measureDrift(new Date(), serverNow));
  });
}
